import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { QrPaymentService } from '../../../../services/qr-payment.service';
import { Subscription } from 'rxjs';
import { AuthSocketService } from '../../../../services/auth-socket.service';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../../../abstraction/auth.service';
import { Utils } from '../../../../utils/Utils';
import { TYPE_MINIAPP } from '../../../../models/Onboarding';
import { ethers } from 'ethers';
import { BalanceService } from '../../../../services/balance.service';

@Component({
  selector: 'app-payment-confirm',
  templateUrl: './payment-confirm.component.html',
  styleUrl: './payment-confirm.component.scss',
})
export class PaymentConfirmComponent implements OnInit, OnDestroy {
  paymentForm: FormGroup;
  private formSubscription?: Subscription;
  private lockResponseSubscription?: Subscription;
  qrPaymentService = inject(QrPaymentService);
  route = inject(ActivatedRoute);
  authSocketService = inject(AuthSocketService);
  authSvc = inject(AuthService);
  balanceService = inject(BalanceService);
  router = inject(Router);

  amountValue: number | string = '';
  lockLogMessage: string = '';
  lockErrorMessage: string = '';
  wldBalance: string = '0';

  constructor() {
    // Initialize form
    const defaultAmount = this.qrPaymentService.paymentAmount();
    const isAmountSet = !!defaultAmount;

    // Initialize amount value
    this.amountValue = defaultAmount || '';

    this.paymentForm = new FormGroup({
      amount: new FormControl(
        defaultAmount || '',
        isAmountSet ? [] : [this.amountValidator.bind(this)],
      ),
    });

    // Initial form state sync
    this.syncFormState();
  }

  ngOnInit(): void {
    // Sync form with service state on init (in case lock response already arrived)
    this.syncFormState();

    // Fetch WLD balance if address already available
    this.loadWldBalance();

    // Subscribe to lock payment responses from service to capture initial lock response
    this.lockResponseSubscription = this.qrPaymentService.lockResponse$.subscribe({
      next: (logData) => {
        if (logData.type === 'success') {
          this.lockLogMessage = 'Lock Response Success: ' + JSON.stringify(logData.data, null, 2);
          this.lockErrorMessage = '';
          // Sync form state after lock response is received to update amount display
          // Use setTimeout to ensure Angular change detection runs
          setTimeout(() => {
            this.syncFormState();
            this.loadWldBalance();
          }, 0);
        } else {
          this.lockErrorMessage = 'Lock Error: ' + JSON.stringify(logData.data, null, 2);
          this.lockLogMessage = '';
        }
      },
    });

    // Also check if amount is already set (in case lock response arrived before subscription)
    // Use a small delay to ensure service state is fully updated
    setTimeout(() => {
      const currentAmount = this.qrPaymentService.paymentAmount();
      if (currentAmount && currentAmount > 0) {
        this.syncFormState();
      }
    }, 100);
  }

  /**
   * Handle amount value change from the phone number input component
   */
  onAmountValueChange(value: number): void {
    this.amountValue = value;
    // Update form control with digit string for validation
    const digitsOnly = value > 0 ? Math.round(value).toString() : '';
    this.paymentForm.get('amount')?.setValue(digitsOnly, { emitEvent: false });
    this.paymentForm.get('amount')?.updateValueAndValidity();
  }

  /**
   * Handle digits change from the phone number input component
   */
  onAmountDigitsChange(digits: string): void {
    // Store raw digits in form control for validation
    this.paymentForm.get('amount')?.setValue(digits, { emitEvent: false });
    this.paymentForm.get('amount')?.updateValueAndValidity();
  }

  /**
   * Sync form state based on service state
   * Called explicitly when state changes
   */
  private syncFormState(): void {
    const amountControl = this.paymentForm.get('amount');
    if (!amountControl) return;

    const serviceAmount = this.qrPaymentService.paymentAmount();
    const isLockedWithAmount = this.qrPaymentService.shouldDisableAmountInput();
    const needsAmount = this.qrPaymentService.qrNeedsAmount();

    if (isLockedWithAmount && serviceAmount) {
      // Amount was set from lock, disable input and clear validators
      this.amountValue = serviceAmount;
      amountControl.setValue(Math.round(serviceAmount).toString(), {
        emitEvent: false,
      });
      amountControl.clearValidators();
      amountControl.disable({ emitEvent: false });
      amountControl.updateValueAndValidity({ emitEvent: false });
    } else if (needsAmount) {
      // Need user to enter amount, enable input and add validators
      this.amountValue = '';
      amountControl.setValidators([this.amountValidator.bind(this)]);
      amountControl.enable({ emitEvent: false });
      amountControl.setValue('', { emitEvent: false });
      amountControl.updateValueAndValidity({ emitEvent: false });
    } else if (serviceAmount) {
      // Amount exists but not locked yet, keep enabled
      this.amountValue = serviceAmount;
      amountControl.setValue(Math.round(serviceAmount).toString(), {
        emitEvent: false,
      });
    }
  }

  /**
   * Custom validator for amount input (validates digit string represents valid amount)
   */
  private amountValidator(control: FormControl): { [key: string]: any } | null {
    const value = control.value;
    if (!value || value === '') {
      return { required: true };
    }

    // Convert digit string to number (treating as base units)
    const numericValue = parseFloat(value);

    if (numericValue < 0.01) {
      return { min: { min: 0.01, actual: numericValue } };
    }

    return null;
  }

  ngOnDestroy(): void {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
    if (this.lockResponseSubscription) {
      this.lockResponseSubscription.unsubscribe();
    }
  }

  getPaymentAmount(): number {
    // Use the amountValue directly if it's a number
    if (typeof this.amountValue === 'number') {
      return this.amountValue;
    }

    // Otherwise get from form control
    const value = this.paymentForm.get('amount')?.value;
    if (!value || value === '') return 0;

    // Value is stored as digit string (representing base units)
    // Convert to number
    const digitsOnly = value.toString().replace(/\D/g, '');
    if (digitsOnly === '') return 0;

    // Return as base units (no conversion needed)
    return parseFloat(digitsOnly);
  }

  /**
   * Get WLD amount from lock response (paymentAssetAmount)
   */
  getWldAmount(): string | null {
    const redirectData = this.qrPaymentService.paymentRedirectData;
    return redirectData?.fiatAmount || null;
  }

  /**
   * Get WLD balance from component property
   */
  getWldBalance(): string {
    return this.wldBalance;
  }

  getPaymentCoin(): string {
    return this.qrPaymentService.paymentRedirectData?.coin || 'WLD';
  }

  getAgainstCurrency(): string {
    return this.qrPaymentService.paymentRedirectData?.against || 'ARS';
  }

  getFiatAmount(): string | null {
    return this.qrPaymentService.paymentRedirectData?.fiatAmount || null;
  }

  getFiatDisplay(): string {
    return this.getFiatAmount() || '...';
  }

  getRecipientDisplay(): string {
    const name = this.qrPaymentService.paymentRecipientName();
    return name ? name : '';
  }

  isConfirmDisabled(): boolean {
    if (this.qrPaymentService.isLoadingLock()) {
      return true;
    }

    if (this.qrPaymentService.qrNeedsAmount()) {
      return !this.paymentForm.valid;
    }

    return false;
  }

  private loadWldBalance(address?: string): void {
    const walletAddress = address || this.qrPaymentService.paymentAddress();
    if (!walletAddress) {
      // Fallback to cached balance if available
      const authBalance = this.authSvc.balanceMiniapp();
      if (authBalance?.WLD) {
        this.wldBalance = authBalance.WLD;
      }
      return;
    }

    this.balanceService
      .getWLDTokenBalance(walletAddress)
      .then((balance) => {
        if (balance) {
          this.wldBalance = balance.WLD || '0';
        } else {
          // If balance is void (e.g., ADDRESS_EVENT), use balance from auth service
          const authBalance = this.authSvc.balanceMiniapp();
          this.wldBalance = authBalance?.WLD || '0';
        }
      })
      .catch((error) => {
        console.error('Error fetching WLD balance:', error);
        const authBalance = this.authSvc.balanceMiniapp();
        this.wldBalance = authBalance?.WLD || '0';
      });
  }

  confirmPayment(): void {
    // If payment is ready to create (locked with amount), redirect with params
    if (this.qrPaymentService.isReadyToCreate()) {
      this.redirectWithParams();
      return;
    }

    // If amount needs to be entered, validate and lock again
    if (this.qrPaymentService.qrNeedsAmount()) {
      if (this.paymentForm.valid) {
        const amount = this.getPaymentAmount();
        // Update service with user-entered amount
        this.qrPaymentService.setPaymentAmount(amount);

        // Lock payment again with the amount
        this.lockErrorMessage = '';
        this.lockLogMessage = 'Locking payment with amount: ' + amount;
        this.qrPaymentService.lockPayment().subscribe({
          next: (response) => {
            if (response) {
              this.lockLogMessage = 'Lock Response Success: ' + JSON.stringify(response, null, 2);
              this.lockErrorMessage = '';
            } else {
              this.lockErrorMessage = 'Lock Response: null (no data returned)';
              this.lockLogMessage = '';
            }
            this.qrPaymentService.handleLockResponse(response);
            // Sync form state after lock response
            this.syncFormState();
            // After successful lock, redirect with params
            if (this.qrPaymentService.isReadyToCreate()) {
              this.redirectWithParams();
            }
          },
          error: (error) => {
            this.lockErrorMessage = 'Lock Error: ' + JSON.stringify(error, null, 2);
            this.lockLogMessage = '';
            console.error('Error locking payment:', error);
          },
        });
      }
    } else if (this.qrPaymentService.isPaymentLocked()) {
      this.redirectWithParams();
    }
  }

  redirectWithParams() {
    const redirectData = this.qrPaymentService.paymentRedirectData;
    if (!redirectData) {
      console.error('No payment redirect data available');
      return;
    }

    const amount = redirectData.amount || this.qrPaymentService.paymentAmount()?.toString() || '0';
    const coin = redirectData.coin || 'WLD';
    const fiatAmount = redirectData.fiatAmount || amount;
    const depositAddress = this.qrPaymentService.DEPOSIT_ADDRESS;
    const against = this.qrPaymentService.AGAINST_CURRENCY;

    const currentState = this.authSvc.onboardingState.value;
    const url = new URLSearchParams({
      appOrigin: currentState.appOrigin,
    }).toString();

    // Get exchange from redirect data, widgetUserObs, or use a fallback
    const exchange =
      redirectData.exchange || this.authSvc.widgetUserObs.value?.exchange || 'ARGENTINA';
    Utils.sendMessageToParent(
      TYPE_MINIAPP.PAY,
      {
        url,
        amount: ethers.parseEther(amount).toString(),
        coin: coin,
        exchange: exchange,
        fiatAmount: fiatAmount,
        depositAddress: depositAddress,
        against: against,
      },
      currentState.appOrigin,
    );
  }

  backButton() {
    this.router.navigate(['payment']);
  }
}
